import type { Course } from "@/services/courses/types/course.types";
import {
  InscriptionStatus,
  type InscriptionStatusResponse,
  ParticipantUiPhase,
} from "@/services/inscription/types";
import { derivePdcCourseListStats } from "@/services/pdcExternal/courses";

/**
 * True when Nest marks completed, or (PDC list on) challenges are done in Reality
 * and all courses are finished according to the PDC list (Nest `user_course_progress` may lag).
 */
export function isMergedRegistrationComplete(
  inscription: InscriptionStatusResponse | null | undefined,
  pdcCourses: Course[] | undefined,
  pdcListEnabled: boolean
): boolean {
  if (!inscription) return false;
  if (inscription.status === InscriptionStatus.COMPLETED) return true;
  if (!pdcListEnabled || pdcCourses === undefined) return false;

  const stats = derivePdcCourseListStats(pdcCourses);
  if (inscription.totalCourses > 0 && stats.totalCoursesCount !== inscription.totalCourses) {
    return false;
  }

  const challengesDone =
    inscription.totalChallenges === 0 ||
    inscription.completedChallengesApproved === inscription.totalChallenges;

  const pdcCoursesDone =
    stats.totalCoursesCount === 0 ||
    stats.completedCoursesCount === stats.totalCoursesCount;

  return challengesDone && pdcCoursesDone;
}

/**
 * Same blocking modal as API `INSCRIPTION_COMPLETED_CELEBRATION` when Reality DB
 * has not yet caught up with PDC course completion.
 */
export function shouldShowPdcLagCelebrationModal(
  inscription: InscriptionStatusResponse | null | undefined,
  pdcCourses: Course[] | undefined,
  pdcListEnabled: boolean,
  isUserOutOfReality?: boolean | null
): boolean {
  if (!inscription) return false;
  if (!pdcListEnabled || pdcCourses === undefined) return false;
  if (isUserOutOfReality === true) return false;
  if (inscription.advancedToResultStage) return false;
  if (inscription.firstStageClosed) return false;
  if (inscription.participantUiPhase !== ParticipantUiPhase.NONE) return false;
  if (inscription.status === InscriptionStatus.COMPLETED) return false;

  return isMergedRegistrationComplete(inscription, pdcCourses, true);
}
